"use client";

import Preloader from "@/app/components/preloader";
import useSearchRouter from "@/app/hooks/useSearchRouter";
import Checkbox from "@/components/design-system/checkbox";
import Pill from "@/components/design-system/pill";
import { useGetUserQuery } from "@/store/userApi";
import { useCallback, useEffect, useState, useTransition } from "react";
import XIcon from "public/next-public/common/icons/x-close-icon.svg";
import styles from "./customFacet.module.scss";

export default function CustomFacet({
  id,
  className,
  name,
  requireAuth,
  hoverText,
  variant = "pill",
}: CustomFacetProps) {
  const { modifyCustomFacet, isCustomFacetActive } = useSearchRouter();
  const { data: user } = useGetUserQuery();
  const [isPending, startTransition] = useTransition();

  const checkedByDefault = isCustomFacetActive(id);

  const [localCheckedState, setLocalCheckedState] = useState(checkedByDefault);

  useEffect(() => {
    setLocalCheckedState(checkedByDefault);
  }, [checkedByDefault]);

  const toggleFilter = useCallback(() => {
    const url = new URL(window.location.href);
    const newState = !localCheckedState;

    if (requireAuth && (!user || user.IsGuestUser)) {
      url.searchParams.set(id, "true");
      window.location.href = `/login?origin=${encodeURIComponent(url.pathname + url.search)}`;
      return;
    }

    setLocalCheckedState(newState);
    startTransition(() => modifyCustomFacet(newState, id));
  }, [localCheckedState, requireAuth, user, modifyCustomFacet, id]);

  if (variant === "checkbox") {
    return (
      <div className={className} title={hoverText}>
        <Checkbox checked={localCheckedState} onChange={toggleFilter} id={id} name={name} label={name} />
      </div>
    );
  }

  if (isPending) {
    return (
      <div className={className}>
        <div className={styles.loadingWrap} role="status" aria-label={`Loading ${name} results`}>
          <Preloader size="small" />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <Pill
        size="medium"
        label={name}
        selected={localCheckedState}
        trailingIcon={localCheckedState ? <XIcon width={16} height={16} aria-hidden /> : undefined}
        ariaPressed={localCheckedState}
        title={hoverText}
        onClick={toggleFilter}
      />
    </div>
  );
}

export type CustomFacetProps = {
  id: string;
  className?: string;
  name: string;
  requireAuth?: boolean;
  hoverText: string;
  /** `checkbox` for the desktop filter sidebar; `pill` for the mobile results chip. */
  variant?: "checkbox" | "pill";
};
